/* eslint-disable no-param-reassign */
import React, { useRef } from 'react';

// Material
import ListItem, { ListItemProps } from '@material-ui/core/ListItem';

// React Dnd
import { useDrag, useDrop } from 'react-dnd';

// Clsx
import clsx from 'clsx';

// Styles
import useStyles from './draggable.styles';

//Lodash
import { debounce } from 'lodash';

interface DraggableProps {
  withoutLimit?: boolean;
  index: number;
  onDropCompleted: () => void;
  onMoveItem: (from: any, to: any) => void;
  canDrag?: boolean;
}

type Props = DraggableProps & ListItemProps;

const Draggable: React.FC<Props> = ({
  withoutLimit,
  index,
  onMoveItem,
  onDropCompleted,
  canDrag = true,
  children,
  className,
  ...rest
}) => {
  const classes = useStyles();
  const ref = useRef<HTMLLIElement>(null);

  const COMPONENT_TYPE = 'LIST_ITEM';

  const [{ isDragging }, dragRef] = useDrag({
    item: { type: COMPONENT_TYPE, index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (_, monitor) => {
      if (!monitor.didDrop()) {
        return;
      }

      const result = monitor.getDropResult();

      if (result) {
        onDropCompleted();
      }
    },
    canDrag,
  });

  const [_, dropRef] = useDrop({
    accept: COMPONENT_TYPE,
    hover: debounce(
      (item: any, monitor) => {
        const draggedIndex = item.index;
        const targetIndex = index;

        if (draggedIndex === targetIndex) {
          return;
        }

        const targetSize = ref.current && ref.current.getBoundingClientRect();
        const targetCenter =
          targetSize && (targetSize.bottom - targetSize.top) / 2;

        const draggedOffset = monitor.getClientOffset();
        const draggedTop =
          draggedOffset && targetSize && draggedOffset.y - targetSize.top;

        if (
          draggedIndex < targetIndex &&
          draggedTop &&
          targetCenter &&
          draggedTop < targetCenter
        ) {
          return;
        }

        if (
          draggedIndex > targetIndex &&
          draggedTop &&
          targetCenter &&
          draggedTop > targetCenter
        ) {
          return;
        }

        onMoveItem(draggedIndex, targetIndex);
        item.index = targetIndex;
      },
      withoutLimit ? undefined : 300,
    ),
  });

  dragRef(dropRef(ref));

  return (
    <ListItem
      innerRef={ref}
      {...(rest as any)}
      className={clsx(className, classes.item, {
        [classes.isDragging]: isDragging,
      })}>
      {children}
    </ListItem>
  );
};

export { Draggable };
export default Draggable;
